import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { ContextOperation } from "../../context/OperationContext";
import LogisticCompoundAction from "../../service/logisticCompound.action";
import FormInput from "../common/FormInput";
import { nameof } from "../common/ObjectUtils";
import { TypesOfData } from "./types";
import * as Mui from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import { customTheme } from "../../../../config/theme/MuiTheme";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import { GenericTitleHeader } from "../common/GenericTitleHeader";
import SectionHeader from "../../../../util/component/SectionHeader";

const LCDetailsPage = () => {
  const { tenant, setLoading } = useContext(ContextOperation);
  const t = useOperationTranslation();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const [first, setFirst] = useState<TypesOfData.IByIdLogisticCompound>();
  const form = useForm<TypesOfData.IByIdLogisticCompound>();
  const re = /^[0-9\b]+$/;
  const isYes: any[] = [
    {
      value: true,
      label: t("internOptionSelectName.true"),
    },
    {
      value: false,
      label: t("internOptionSelectName.false"),
    },
  ];

  const currentStatusOption = [
    {
      value: "Activated",
      label: t("logisticCompound.option.activated"),
    },
    {
      value: "Terminated",
      label: t("logisticCompound.option.terminated"),
    },
  ];

  const onSubmit = (data: TypesOfData.IByIdLogisticCompound) => {
    updateLogisticCompound(Number(id), tenant, data);
  };
  function emptyStringToNullOrNumber(value: any) {
    return value === "" ? null : +value;
  }

  function updateLogisticCompound(
    id: number,
    tenant: string,
    data: TypesOfData.IByIdLogisticCompound
  ) {
    setLoading(true);
    LogisticCompoundAction()
      .editLogisticCompound(id, tenant, data)
      .then((response: any) => {
        snackbar.show(
          "success",
          t("logisticCompound.editModal.message.success")
        );
        setLoading(false);
        navigate("/operation/suppliers/logisticCompounds");
      })
      .catch(() => {
        setLoading(false);
        snackbar.show("error", t("logisticCompound.editModal.message.error"));
      });
  }
  useEffect(() => {
    if (!id) return;
    LogisticCompoundAction()
      .getByIdLogisticCompound(Number(id), tenant)
      .then((response) => setFirst(response));
  }, []);

  useEffect(() => {
    if (first) {
      form.reset({
        currentStatus: first.currentStatus,
        currentAttribute: first.currentAttribute,
        city17: first.city17,
        brandName: first.brandName,
        chargingSite: !!first.chargingSite ? true : false,
        deliveryCollection: first.deliveryCollection,
        note: first.note,
        howToIta: first.howToIta,
        howToEng: first.howToEng,
        openingClosingHours: first.openingClosingHours,
        openingTimeMonFri: first.openingTimeMonFri,
        openingTimeSat: first.openingTimeSat,
        openingTimeSun: first.openingTimeSun,
        financial: {
          representativeAccountCode: first.financial.representativeAccountCode,
          clientIdentificationCode: first.financial.clientIdentificationCode,
          clientAccountCode: first.financial.clientAccountCode,
          zone: first.financial.zone,
          feesDued: !!first.financial.feesDued
            ? first.financial.feesDued
            : false,
          rentalFiscalReceiptsCode: first.financial.rentalFiscalReceiptsCode,
          feesFiscalReceiptsCode: first.financial.feesFiscalReceiptsCode,
        },
        eparking: {
          identificationCode: first.eparking.identificationCode,
          status: first.eparking.status,
          address: first.eparking.address,
          streetNumber: first.eparking.streetNumber,
          certificationProtocolIdentificationCode:
            first.eparking.certificationProtocolIdentificationCode,
          evaluationOutcome: first.eparking.evaluationOutcome,
          note: first.eparking.note,
          requiredPower: first.eparking.requiredPower, //(solo campo numerico)
          effectivePower: first.eparking.effectivePower, //(solo campo numerico)
          chargingColumns: first.eparking.chargingColumns, //(solo campo numerico)
          chargingColumnId: first.eparking.chargingColumnId, //(solo campo numerico)
          chargingColumnType: first.eparking.chargingColumnType,
          chargingPoints: first.eparking.chargingPoints, //(solo campo numerico)
        },
      });
    }
  }, [first]);

  return (
    <Mui.Box bgcolor={"inherit"}>
      <SectionHeader
          title={t("logisticCompound.editModal.titleFirst") + " " + t("logisticCompound.editModal.titleSecond")}
      />
      <Mui.Box
        m={2}
        p={2}
        border={1}
        borderColor={customTheme.palette.neutral.dark}
        borderRadius={1}
      >
        <Mui.Box mb={2}>
          <Mui.Stack direction="row" alignItems="center" gap={1}>
            <Mui.Button
              variant="text"
              size="icon"
              onClick={() =>
                navigate("/operation/suppliers/logisticCompounds")
              }
              color={"primary"}
            >
              <MuiIcons.ArrowBack />
            </Mui.Button>
            <Mui.Typography variant="h5">
              {t("logisticCompound.editModal.titleBack")}
            </Mui.Typography>
          </Mui.Stack>
        </Mui.Box>
        <Mui.Box>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Mui.Box mb={2}>
              <Mui.Card>
                <Mui.CardHeader
                  title={t("logisticCompound.editModal.lc.title")}
                />
                <Mui.CardContent>
                  <Mui.Grid container columns={12} spacing={2.5}>
                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="select"
                        options={currentStatusOption}
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "currentStatus"
                        )}`}
                        fullWidth
                        label={t("logisticCompound.editModal.lc.currentStatus")}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={nameof<TypesOfData.IByIdLogisticCompound>(
                          "city17"
                        )}
                        fullWidth
                        label={t("logisticCompound.editModal.lc.city17")}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={nameof<TypesOfData.IByIdLogisticCompound>(
                          "brandName"
                        )}
                        fullWidth
                        label={t("logisticCompound.editModal.lc.brandName")}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="select"
                        name={nameof<TypesOfData.IByIdLogisticCompound>(
                          "chargingSite"
                        )}
                        fullWidth
                        options={isYes}
                        label={t("logisticCompound.editModal.lc.chargingSite")}
                        //formInstance={form}
                        value={form.watch("chargingSite")}
                        onChange={(newStatusValue) => {
                          form.setValue("chargingSite", newStatusValue);
                        }}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={nameof<TypesOfData.IByIdLogisticCompound>(
                          "deliveryCollection"
                        )}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.lc.deliveryCollection"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={nameof<TypesOfData.IByIdLogisticCompound>("note")}
                        fullWidth
                        label={t("logisticCompound.editModal.lc.note")}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={nameof<TypesOfData.IByIdLogisticCompound>(
                          "howToIta"
                        )}
                        fullWidth
                        label={t("logisticCompound.editModal.lc.howToIta")}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={nameof<TypesOfData.IByIdLogisticCompound>(
                          "howToEng"
                        )}
                        fullWidth
                        label={t("logisticCompound.editModal.lc.howToEng")}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={nameof<TypesOfData.IByIdLogisticCompound>(
                          "openingClosingHours"
                        )}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.lc.openingClosingHours"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={nameof<TypesOfData.IByIdLogisticCompound>(
                          "openingTimeMonFri"
                        )}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.lc.openingTimeMonFri"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={nameof<TypesOfData.IByIdLogisticCompound>(
                          "openingTimeSat"
                        )}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.lc.openingTimeSat"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={nameof<TypesOfData.IByIdLogisticCompound>(
                          "openingTimeSun"
                        )}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.lc.openingTimeSun"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>
                  </Mui.Grid>
                </Mui.CardContent>
              </Mui.Card>
            </Mui.Box>

            {/* E parking */}

            <Mui.Box mb={2}>
              <Mui.Card>
                <Mui.CardHeader
                  title={t("logisticCompound.editModal.eParking.title")}
                />
                <Mui.CardContent>
                  <Mui.Grid container columns={12} spacing={2.5}>
                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>(
                          "identificationCode"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.eParking.identificationCode"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>("status")}`}
                        fullWidth
                        label={t("logisticCompound.editModal.eParking.status")}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>("address")}`}
                        fullWidth
                        label={t("logisticCompound.editModal.eParking.address")}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>("streetNumber")}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.eParking.streetNumber"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>(
                          "certificationProtocolIdentificationCode"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.eParking.certificationProtocolIdentificationCode"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>(
                          "evaluationOutcome"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.eParking.evaluationOutcome"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>("note")}`}
                        fullWidth
                        label={t("logisticCompound.editModal.eParking.note")}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="number"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>("requiredPower")}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.eParking.requiredPower"
                        )}
                        //formInstance={form}
                        value={`${form.watch("eparking.requiredPower")}` || ""}
                        onChange={(newStatusValue: any) => {
                          if (
                            newStatusValue === "" ||
                            re.test(newStatusValue)
                          ) {
                            form.setValue(
                              "eparking.requiredPower",
                              emptyStringToNullOrNumber(newStatusValue)
                            );
                          }
                        }}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="number"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>(
                          "effectivePower"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.eParking.effectivePower"
                        )}
                        //formInstance={form}
                        value={`${form.watch("eparking.effectivePower")}` || ""}
                        onChange={(newStatusValue: any) => {
                          if (
                            newStatusValue === "" ||
                            re.test(newStatusValue)
                          ) {
                            form.setValue(
                              "eparking.effectivePower",
                              emptyStringToNullOrNumber(newStatusValue)
                            );
                          }
                        }}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="number"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>(
                          "chargingColumns"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.eParking.chargingColumns"
                        )}
                        //formInstance={form}
                        value={
                          `${form.watch("eparking.chargingColumns")}` || ""
                        }
                        onChange={(newStatusValue: any) => {
                          if (
                            newStatusValue === "" ||
                            re.test(newStatusValue)
                          ) {
                            form.setValue(
                              "eparking.chargingColumns",
                              emptyStringToNullOrNumber(newStatusValue)
                            );
                          }
                        }}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="number"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>(
                          "chargingColumnId"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.eParking.chargingColumnId"
                        )}
                        //formInstance={form}
                        value={
                          `${form.watch("eparking.chargingColumnId")}` || ""
                        }
                        onChange={(newStatusValue: any) => {
                          if (
                            newStatusValue === "" ||
                            re.test(newStatusValue)
                          ) {
                            form.setValue(
                              "eparking.chargingColumnId",
                              emptyStringToNullOrNumber(newStatusValue)
                            );
                          }
                        }}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>(
                          "chargingColumnType"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.eParking.chargingColumnType"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="number"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "eparking"
                        )}.${nameof<TypesOfData.IEParkingLC>(
                          "chargingPoints"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.eParking.chargingPoints"
                        )}
                        //formInstance={form}
                        value={`${form.watch("eparking.chargingPoints")}` || ""}
                        onChange={(newStatusValue: any) => {
                          if (
                            newStatusValue === "" ||
                            re.test(newStatusValue)
                          ) {
                            form.setValue(
                              "eparking.chargingPoints",
                              emptyStringToNullOrNumber(newStatusValue)
                            );
                          }
                        }}
                      />
                    </Mui.Grid>
                  </Mui.Grid>
                </Mui.CardContent>
              </Mui.Card>
            </Mui.Box>

            {/* Finance */}

            <Mui.Box mb={2}>
              <Mui.Card>
                <Mui.CardHeader
                  title={t("logisticCompound.editModal.financial.title")}
                />
                <Mui.CardContent>
                  <Mui.Grid container columns={12} spacing={2.5}>
                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "financial"
                        )}.${nameof<TypesOfData.IFinancialLC>(
                          "representativeAccountCode"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.financial.representativeAccountCode"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "financial"
                        )}.${nameof<TypesOfData.IFinancialLC>(
                          "clientIdentificationCode"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.financial.clientIdentificationCode"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "financial"
                        )}.${nameof<TypesOfData.IFinancialLC>(
                          "clientAccountCode"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.financial.clientAccountCode"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "financial"
                        )}.${nameof<TypesOfData.IFinancialLC>("zone")}`}
                        fullWidth
                        label={t("logisticCompound.editModal.financial.zone")}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="select"
                        options={isYes}
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "financial"
                        )}.${nameof<TypesOfData.IFinancialLC>("feesDued")}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.financial.feesDued"
                        )}
                        //formInstance={form}
                        value={form.watch("financial.feesDued")}
                        onChange={(newStatusValue) => {
                          form.setValue("financial.feesDued", newStatusValue);
                        }}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "financial"
                        )}.${nameof<TypesOfData.IFinancialLC>(
                          "rentalFiscalReceiptsCode"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.financial.rentalFiscalReceiptsCode"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>

                    <Mui.Grid item xl={3} lg={3} md={6} xs={12}>
                      <FormInput
                        type="text"
                        name={`${nameof<TypesOfData.IByIdLogisticCompound>(
                          "financial"
                        )}.${nameof<TypesOfData.IFinancialLC>(
                          "feesFiscalReceiptsCode"
                        )}`}
                        fullWidth
                        label={t(
                          "logisticCompound.editModal.financial.feesFiscalReceiptsCode"
                        )}
                        formInstance={form}
                      />
                    </Mui.Grid>
                  </Mui.Grid>
                </Mui.CardContent>
              </Mui.Card>
            </Mui.Box>
            {/* Button */}
            <Mui.Stack
              direction={"row"}
              spacing={2.5}
              justifyContent={"flex-end"}
            >
              <Mui.Button
                variant="outlined"
                size="medium"
                onClick={() => form.reset()}
              >
                <Mui.Typography color={customTheme.palette.accent.main}>
                  {t("actionButtons.reset")}
                </Mui.Typography>
              </Mui.Button>
              <Mui.Button
                variant="contained"
                size="medium"
                type="submit"
                //onClick={() => alert('save')}
              >
                <Mui.Typography color={customTheme.palette.accent.contrastText}>
                  {t("actionButtons.save")}
                </Mui.Typography>
              </Mui.Button>
            </Mui.Stack>
          </form>
        </Mui.Box>
      </Mui.Box>
    </Mui.Box>
  );
};

export default LCDetailsPage;
